<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="py-3"
    >
      <BCol
        cols="12"
        md="10"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="gridOfficerTasks.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="2"
      >
        <BFormGroup
          label="Month"
          label-for="filterMonth"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <flat-pickr
            id="filterMonth"
            v-model="gridOfficerTasks.filter.month"
            class="form-control"
            placeholder="--------- ----"
            :config="flatPickerConfig"
          />
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
        md="6"
        class="d-flex gap-2 mb-2"
      >
        <BDropdown
          id="dropdown-offset"
          text="Import Tasks"
          variant="primary"
        >
          <BDropdownItem
            @click.prevent="importMultipleTasks"
          >
            Multiple Officers
          </BDropdownItem>
          <BDropdownItem
            @click.prevent="importSingleTasks"
          >
            Single Officer
          </BDropdownItem>
        </BDropdown>
        <BDropdown
          id="dropdown-offset"
          text="Download Template"
          variant="dark"
        >
          <template #button-content>
            <span v-if="downloading.multiple || downloading.single">
              <BSpinner
                small
                class="mr-2"
              />
              Downloading
            </span>
            <span v-else>
              Download Template
            </span>
          </template>
          <BDropdownItem
            @click.prevent="downloadMultipleTemplate"
          >
            <BSpinner
              v-if="downloading.multiple"
              small
              class="mr-2"
            /> {{ downloading.multiple ? 'Downloading' : '' }} Multiple Officers
          </BDropdownItem>
          <BDropdownItem
            @click.prevent="openSingleTemplate"
          >
            <BSpinner
              v-if="downloading.single"
              small
              class="mr-2"
            /> {{ downloading.single ? 'Downloading ' : '' }} Single Officer
          </BDropdownItem>
        </BDropdown>
      </BCol>
    </BRow>

    <BRow
      v-if="gridOfficerTasks.busy"
      class="gap-y-6 mb-3"
    >
      <BCol
        cols="12"
      >
        <div class="text-center py-5">
          <BIcon
            icon="stopwatch"
            font-scale="5"
            animation="cylon"
          />
          <p class="h3 py-2">
            <strong>Loading . . .</strong>
          </p>
        </div>
      </BCol>
    </BRow>

    <BRow
      v-else-if="Number(gridOfficerTasks.totalRows) <= 0 && !gridOfficerTasks.busy"
    >
      <BCol
        cols="12"
        class="text-center py-5"
      >
        <BIcon
          icon="calendar"
          font-scale="5"
        />
        <p class="h3 py-2">
          <strong>No officer tasks found</strong>
        </p>
      </BCol>
    </BRow>

    <BRow
      v-else
      class="gap-y-6"
    >
      <BCol
        v-for="officer in gridOfficerTasks.rows"
        :key="officer?.id"
        cols="12"
        md="4"
      >
        <OfficerTask
          :officer="{
            first_name: officer.first_name,
            last_name: officer.last_name,
            clusters: officer?.clusters || [],
            employee_id: officer.sso_id,
            total_buyers: officer?.customers_bought_count,
            photo_url: officer?.photo_url,
          }"
          :current-progress="{
            event: officer.events_count,
            customer: officer.customers_attended_count,
            hectare: officer.total_actual_hectares,
            cost: officer.total_actual_costs,
            sales: officer.total_actual_sales
          }"
          :total-progress="{
            event: officer.event_plan,
            customer: officer.total_customer_plan,
            hectare: officer.total_hectare_plan,
            cost: officer.total_budget_plan,
            sales: officer.total_sales_plan
          }"
        >
          <template #footer>
            <div class="d-flex justify-content-end gap-2 mt-3">
              <BButton
                size="sm"
                variant="outline-dark"
                :to="{
                  name: 'asm.tracking.officer-tasks',
                  params: {
                    officer: officer.id
                  }
                }"
              >
                View Tasks
                <i class="ml-1 far fa-arrow-right" />
              </BButton>
            </div>
          </template>
        </OfficerTask>
      </BCol>
    </BRow>

    <BModal
      ref="modalDownloadSingle"
      title="Download Template"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
    >
      <ValidationObserver
        ref="formDownload"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="month"
                name="month"
                rules="required"
              >
                <BFormGroup
                  label="Month"
                  label-for="fMonth"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <flat-pickr
                    id="fMonth"
                    v-model="formDownload.month"
                    :class="['form-control', {
                      'is-invalid': errors.length > 0
                    }]"
                    placeholder="--------- ----"
                    :config="{
                      ...flatPickerConfig,
                      altInput: false
                    }"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="officer"
                name="officer"
                rules="required"
              >
                <BFormGroup
                  label="Officer"
                  label-for="fOfficer"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.fsOfficer"
                    input-id="fOfficer"
                    label="officer_name"
                    clearable
                    append-to-body
                    placeholder="select officer here"
                    :options="options.fsOfficers"
                    :disabled="[
                      state.busy,
                      fetching.fsOfficers
                    ].some(isTrue => isTrue)"
                    :loading="fetching.fsOfficers"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                    @search="searchFSOfficers"
                  >
                    <template #option="{ officer_name, sso_id }">
                      <div
                        class="text-break text-wrap"
                      >
                        <strong class="text-primary">{{ sso_id }}</strong> - {{ officer_name }}
                      </div>
                    </template>
                    <template #selected-option="{ officer_name, sso_id }">
                      <div
                        class="text-break text-wrap"
                      >
                        <strong class="text-primary">{{ sso_id }}</strong> - {{ officer_name }}
                      </div>
                    </template>
                    <template #no-options="">
                      no available officer
                    </template>
                  </VSelect>
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

          </BRow>

        </form>
      </ValidationObserver>
      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy || downloading.single"
          @click.prevent="downloadSingleTemplate"
        >
          <BSpinner
            v-if="downloading.single"
            small
            class="mr-2"
          /> {{ downloading.single ? 'Downloading' : 'Download' }} Template
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>

    </BModal>

    <SingleUploader
      ref="singleUploader"
      @imported="gridProvider"
    />

    <MultipleUploader
      ref="multipleUploader"
      @imported="gridProvider"
    />
  </BContainer>
</template>
<script>
import { mapGetters } from 'vuex'
import { setTableOptions, getTableOptions } from '@/forage/asm/tasks'
import MOMENT from 'moment'
// import QUERY from 'lodash'
import FPMonthSelect from 'flatpickr/dist/plugins/monthSelect'

import OfficerTask from '@/views/asm/tasks/components/OfficerTask.vue'
import SingleUploader from '@/views/asm/tasks/components/SingleUploader.vue'
import MultipleUploader from '@/views/asm/tasks/components/MultipleUploader.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MClusters from '@/mixins/locations/clusters'
import MFSOfficers from '@/mixins/accounts/fsos'

import { SASMFsoTask } from '@/services/asm'

export default {
  name: 'ASMTasksBase',

  components: {
    OfficerTask,
    SingleUploader,
    MultipleUploader
  },

  mixins: [
    MxMisc,
    MxFormatter,
    MClusters,
    MFSOfficers
  ],

  beforeRouteEnter (_to, _from, navigate) {
    getTableOptions().then(options => {
      navigate(context => {
        if (context.currentUser) {
          context.state.settings = true

          context.selected.team = {
            id: context.currentUser?.team || ''
          }

          context.gridOfficerTasks.selected.team = {
            id: context.currentUser?.team || ''
          }

          context.gridOfficerTasks.selected.area = {
            id: context.currentUser?.area || ''
          }

          if (options) {
            const { currentPage, perPage, sortBy, sortDesc, totalRows, filter } = options

            context.gridOfficerTasks = Object.assign(context.gridOfficerTasks, {
              totalRows: totalRows || 0,
              sortBy: sortBy || 'id',
              sortDesc: sortDesc || false,
              perPage: perPage || 10,
              currentPage: currentPage || 0,
              filter: {
                search: filter?.search || '',
                month: filter?.month || '',
                cluster: ''
              }
              // editable: {
              //   cluster: filter?.cluster || ''
              // }
            })
          } else {
            context.gridOfficerTasks.currentPage = 1
          }

          context.$nextTick(() => {
          //   context.getClusters(context.gridOfficerTasks, '', context.currentUser.area).finally(() => {
          //     if (context.gridOfficerTasks.editable.cluster) {
          //       context.gridOfficerTasks.selected.cluster = QUERY.find(context.gridOfficerTasks.options.clusters, {
          //         id: context.gridOfficerTasks.editable.cluster
          //       }) || ''
          //     }
            context.state.settings = false
            context.gridProvider()
          //   })
          })
        }
      })
    })
  },

  middleware: [
    'auth',
    'asm'
  ],

  metaInfo: () => ({
    title: 'Officer Tasks'
  }),

  data () {
    return {
      flatPickerConfig: {
        appendTo: document.body,
        altInput: true,
        allowInput: true,
        disableMobile: true,
        plugins: [
          new FPMonthSelect({
            dateFormat: 'Y-m',
            altFormat: 'F Y'
          })
        ]
      },
      downloading: {
        single: false,
        multiple: false
      },
      state: {
        busy: false,
        editing: false,
        settings: false
      },
      selected: {
        team: {
          id: ''
        },
        activityType: 'Event'
      },
      formDownload: {
        month: MOMENT().format('YYYY-MM'),
        sso_id: '',
        first_name: '',
        last_name: ''
      },
      gridOfficerTasks: {
        busy: false,
        fetching: {
          clusters: false
        },
        options: {
          clusters: []
        },
        selected: {
          team: {
            id: ''
          },
          area: {
            id: ''
          },
          cluster: ''
        },
        editable: {
          cluster: ''
        },
        filter: {
          search: '',
          month: MOMENT().format('YYYY-MM'),
          cluster: ''
        },
        filterOn: [],
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        rows: []
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    })
  },

  watch: {

    'selected.fsOfficer' (officer) {
      this.formDownload.sso_id = officer?.sso_id || ''
      this.formDownload.first_name = officer?.first_name || ''
      this.formDownload.last_name = officer?.last_name || ''
    },

    'gridOfficerTasks.selected.cluster' (cluster) {
      this.gridOfficerTasks.filter.cluster = cluster?.id || ''
    },

    'gridOfficerTasks.filter': {
      handler () {
        if (!this.state.settings) {
          this.gridProvider()
        }
      },
      deep: true
    }
  },

  methods: {

    async gridProvider () {
      this.gridOfficerTasks.busy = true

      const payload = {
        page: this.gridOfficerTasks.currentPage,
        per_page: this.gridOfficerTasks.perPage,
        filter_text: this.gridOfficerTasks.filter.search,
        schedule: this.gridOfficerTasks.filter.month,
        area_id: this.currentUser.area,
        // cluster_id: this.gridOfficerTasks.filter.cluster,
        display: 'group'
      }

      return SASMFsoTask.get(payload).then(
        ({ data }) => {
          const totalRows = Array.from(data || []).length
          this.gridOfficerTasks.totalRows = totalRows
          this.gridOfficerTasks.rows = data

          setTableOptions({
            currentPage: this.gridOfficerTasks.currentPage,
            perPage: this.gridOfficerTasks.perPage,
            sortBy: this.gridOfficerTasks.sortBy,
            sortDesc: this.gridOfficerTasks.sortDesc,
            totalRows: totalRows,
            filter: this.gridOfficerTasks.filter
          })
          return data
        }
      ).catch(
        () => {
          this.gridOfficerTasks.totalRows = 1
          setTableOptions({
            currentPage: this.gridOfficerTasks.currentPage,
            perPage: this.gridOfficerTasks.perPage,
            sortBy: this.gridOfficerTasks.sortBy,
            sortDesc: this.gridOfficerTasks.sortDesc,
            totalRows: 0,
            filter: this.gridOfficerTasks.filter
          })
          return []
        }
      ).finally(() => {
        this.gridOfficerTasks.busy = false
      })
    },

    async searchFilterClusters (search, loading) {
      this.searchClustersProvider(this.gridOfficerTasks, search, loading)
    },

    importMultipleTasks () {
      this.$refs.multipleUploader.importTasks()
    },

    downloadMultipleTemplate () {
      this.downloading.multiple = true
      this.$refs.multipleUploader.downloadTemplate(this.gridOfficerTasks.filter.month).finally(() => {
        this.downloading.multiple = false
      })
    },

    importSingleTasks () {
      this.$refs.singleUploader.importTasks()
    },

    openSingleTemplate () {
      this.formDownload = {
        month: MOMENT().format('YYYY-MM'),
        sso_id: '',
        first_name: '',
        last_name: ''
      }

      this.getFSOfficers(this, '', this.currentUser.team, this.currentUser.area)

      this.$refs.modalDownloadSingle.show()
    },

    downloadSingleTemplate () {
      this.$refs.formDownload.validate().then(
        validDownload => {
          if (!validDownload) {
            return this.swalInvalid()
          }

          this.downloading.single = true
          this.$refs.singleUploader.downloadTemplate(this.formDownload, this.formDownload.month).finally(() => {
            this.downloading.single = false
            this.$refs.modalDownloadSingle.hide()
          })
        }
      )
    }
  }
}
</script>
